import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AdsStudioAssetGridCell,
  GridSectionName,
  SectionTilesGrid,
  DEFAULT_PAGE_SIZE,
  TilesGridLoadingCell,
  AdsStudioSpinner,
  ManualAddedImageSource,
  getUpdatedAssets,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

export const ImageGridView = ({
  isSearchResultPage,
  i18n,
  dataService,
  logger,
  onFailedToLoadData,
  searchText,
  navigation,
  cropDataService,
}) => {
  const {
    assets,
    totalAssetCount,
    totalLibraryAssetCount,
    loadMoreData,
    renameAsset,
  } = dataService;

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { imageCroppingTasks } = cropDataService;

  const loadedAssetsList = useMemo(() => {
    const updatedAssets = getUpdatedAssets({
      assets,
      updates: {
        imageCroppingTasks,
      },
    });

    const updatedAssetsWithAriaLabel = _.map(updatedAssets, (asset) => {
      if (_.isObject(asset)) {
        return ({
          ...asset,
          ariaLabel: i18n.getString(_TL_('Image preview')),
        });
      }
      return asset;
    });

    return updatedAssetsWithAriaLabel;
  }, [assets, i18n, imageCroppingTasks]);

  const imagesGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionClassName: 'ads-studio-image-section',
    itemKey: 'id',
    items: loadedAssetsList,
    totalItemCount: totalAssetCount,
    config: {
      scrollContainer: '[role="main"]',
      minItemHeight: 42,
    },
  }), [loadedAssetsList, totalAssetCount]);

  const sectionGridData = useMemo(
    () => ([imagesGridSection]),
    [imagesGridSection]
  );

  const hasLibraryAssets = useMemo(() => _.some(assets, item => item && item.manualAddedImageSourceType === ManualAddedImageSource.Library), [assets]);

  const libraryAssets = useMemo(() => (_.filter(assets, item =>
    item && (_.isUndefined(item.manualAddedImageSourceType) || item.manualAddedImageSourceType === ManualAddedImageSource.Library))), [assets]);

  const onRenderCell = useCallback(({ item }) => {
    const {
      _index,
      id,
      folderAssetType,
      manualAddedImageSourceType,
      url,
    } = item || {};

    const onCellDetailsClick = () => {
      navigation.gotoAssetDetails({
        assetId: id,
        assets: libraryAssets,
        totalAssetCount: hasLibraryAssets ? totalLibraryAssetCount : totalAssetCount,
        folderAssetType,
        loadMoreData,
        renameAsset,
      });
    };
    const onCellEditClick = () => {
      const editImageUrl = manualAddedImageSourceType === ManualAddedImageSource.ShutterStock ? url : undefined;
      if (logger && logger.logTrace) {
        logger.logTrace('Image Details: edit image', { assetId: id });
      }
      navigation.gotoEditImagePage({
        imageId: id,
        editImageUrl,
        query: isSearchResultPage ? searchText : undefined,
      });
    };

    return (
      <AdsStudioAssetGridCell
        item={item}
        onDetailsClick={onCellDetailsClick}
        onEditClick={onCellEditClick}
        index={_index}
        i18n={i18n}
        highlightedText={searchText}
        logger={logger}
      />
    );
  }, [hasLibraryAssets, i18n, isSearchResultPage, libraryAssets, loadMoreData, logger, navigation, renameAsset, searchText, totalAssetCount, totalLibraryAssetCount]);

  const loadNextPage = useCallback(() => {
    setIsLoadingMore(true);
    loadMoreData({ count: DEFAULT_PAGE_SIZE })
      .catch((err) => {
        onFailedToLoadData(err);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [loadMoreData, onFailedToLoadData]);

  const onRenderLoadingCell = useCallback(({ items, totalItemCount }) => {
    if ((items.length < totalItemCount) && (isSearchResultPage || items.length > 0)) {
      return (
        <TilesGridLoadingCell onLoading={loadNextPage} key={items.length} >
          {isLoadingMore && (
            <div className="section-loading">
              <AdsStudioSpinner useCustomSize />
            </div>
          )}
        </TilesGridLoadingCell>
      );
    }
    return null;
  }, [isLoadingMore, isSearchResultPage, loadNextPage]);

  useEffect(() => {
    if (navigation) {
      navigation.updateAssetDetailsParams({ assets: libraryAssets });
    }
  }, [libraryAssets, navigation]);

  return (
    <SectionTilesGrid
      sectionData={sectionGridData}
      onRenderCell={onRenderCell}
      onRenderLoadingCell={onRenderLoadingCell}
    />
  );
};

ImageGridView.propTypes = {
  isSearchResultPage: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({}),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  onFailedToLoadData: PropTypes.func,
  searchText: PropTypes.string,
  navigation: PropTypes.shape({}).isRequired,
  cropDataService: PropTypes.shape({
    imageCroppingTasks: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

ImageGridView.defaultProps = {
  isSearchResultPage: false,
  dataService: {},
  onFailedToLoadData: _.noop,
  searchText: '',
};
